import {Component, Input, OnInit} from '@angular/core';
import {UntypedFormControl, UntypedFormGroup, Validators} from "@angular/forms";
import {ErrorStateMatcher} from '@angular/material/core';
import {UserService} from "app/core/user/user.service";
import {ExtProfilesService} from "../../ext-profiles.service";
import {UntilDestroy} from "@ngneat/until-destroy";
import { IUser } from 'app/interfaces/IUser'

@UntilDestroy()
@Component({
    selector: 'app-etat-province-usa-canada',
    templateUrl: './etat-province-usa-canada.component.html',
    styleUrls: ['./etat-province-usa-canada.component.scss'],
    standalone: false
})
export class EtatProvinceUsaCanadaComponent implements OnInit {

    cUser: IUser;
    @Input() keyname: string;
    @Input() curLang: string;
    @Input() displayDebug: false;

    public fGroup: UntypedFormGroup = new UntypedFormGroup({
        'internal_country': new UntypedFormControl('', Validators.required),
        'etat_province_usa_canada': new UntypedFormControl('', Validators.required),
    });

    constructor(
        public userService: UserService,
        private extProfilesService: ExtProfilesService,
        private errorStateMatcher: ErrorStateMatcher,
    ) {}

    /** Même logique que mat-error + mat-select : évite de placer le message sous le trigger (chevauchement avec le panneau). */
    countryRequiredErrorVisible(): boolean {
        const c = this.fGroup.get('internal_country');
        return !!(c?.hasError('required') && this.errorStateMatcher.isErrorState(c, null));
    }

    ngOnInit() {
        this.fGroup.patchValue({
            'internal_country': this.userService.user.extProfiles['internal_country'],
            'etat_province_usa_canada': this.userService.user.extProfiles['etat_province_usa_canada'],
        }, {emitEvent: false})
    }

    chooseState(country, state) {
        this.fGroup.get('etat_province_usa_canada').patchValue(`${country}-${state}`);
        this.extProfilesService.updateMultipleExtProfiles(this.fGroup.getRawValue());
    }

    //
    // Constants....
    //
    countries = [
        {
            abbreviation: 'CA',
            country: 'Canada',
        },
        {
            abbreviation: 'US',
            country: {
                fr: 'États-Unis',
                en: 'United States'
            },
        },
    ]

    states = {
        'CA': [
            {
                "name": "Alberta",
                "abbreviation": "AB"
            },
            {
                "name": {
                    fr: "Colombie-Britannique",
                    en: "British Columbia"
                },
                "abbreviation": "BC"
            },
            {
                "name": "Manitoba",
                "abbreviation": "MB"
            },
            {
                "name": {
                    fr: "Nouveau-Brunswick",
                    en: "New Brunswick"
                },
                "abbreviation": "NB"
            },
            {
                "name": {
                    fr: "Terre-Neuve-et-Labrador",
                    en: "Newfoundland and Labrador"
                },
                "abbreviation": "NL"
            },
            {
                "name": {
                    fr: "Territoires du Nord-Ouest",
                    en: "Northwest Territories"
                },
                "abbreviation": "NT"
            },
            {
                "name": {
                    fr: "Nouvelle-Écosse",
                    en: "Nova Scotia"
                },
                "abbreviation": "NS"
            },
            {
                "name": "Nunavut",
                "abbreviation": "NU"
            },
            {
                "name": "Ontario",
                "abbreviation": "ON"
            },
            {
                "name": {
                    fr: "Île-du-Prince-Édouard",
                    en: "Prince Edward Island"
                },
                "abbreviation": "PE"
            },
            {
                "name": {
                    fr: "Québec",
                    en: "Quebec"
                },
                "abbreviation": "QC"
            },
            {
                "name": "Saskatchewan",
                "abbreviation": "SK"
            },
            {
                "name": {
                    fr: "Yukon",
                    en: "Yukon Territory"
                },
                "abbreviation": "YT"
            }
        ],
        'US': [
            {
                "name": "Alabama",
                "abbreviation": "AL"
            },
            {
                "name": "Alaska",
                "abbreviation": "AK"
            },
            {
                "name": "American Samoa",
                "abbreviation": "AS"
            },
            {
                "name": "Arizona",
                "abbreviation": "AZ"
            },
            {
                "name": "Arkansas",
                "abbreviation": "AR"
            },
            {
                "name": "California",
                "abbreviation": "CA"
            },
            {
                "name": "Colorado",
                "abbreviation": "CO"
            },
            {
                "name": "Connecticut",
                "abbreviation": "CT"
            },
            {
                "name": "Delaware",
                "abbreviation": "DE"
            },
            {
                "name": "District Of Columbia",
                "abbreviation": "DC"
            },
            {
                "name": "Federated States Of Micronesia",
                "abbreviation": "FM"
            },
            {
                "name": "Florida",
                "abbreviation": "FL"
            },
            {
                "name": "Georgia",
                "abbreviation": "GA"
            },
            {
                "name": "Guam",
                "abbreviation": "GU"
            },
            {
                "name": "Hawaii",
                "abbreviation": "HI"
            },
            {
                "name": "Idaho",
                "abbreviation": "ID"
            },
            {
                "name": "Illinois",
                "abbreviation": "IL"
            },
            {
                "name": "Indiana",
                "abbreviation": "IN"
            },
            {
                "name": "Iowa",
                "abbreviation": "IA"
            },
            {
                "name": "Kansas",
                "abbreviation": "KS"
            },
            {
                "name": "Kentucky",
                "abbreviation": "KY"
            },
            {
                "name": "Louisiana",
                "abbreviation": "LA"
            },
            {
                "name": "Maine",
                "abbreviation": "ME"
            },
            {
                "name": "Marshall Islands",
                "abbreviation": "MH"
            },
            {
                "name": "Maryland",
                "abbreviation": "MD"
            },
            {
                "name": "Massachusetts",
                "abbreviation": "MA"
            },
            {
                "name": "Michigan",
                "abbreviation": "MI"
            },
            {
                "name": "Minnesota",
                "abbreviation": "MN"
            },
            {
                "name": "Mississippi",
                "abbreviation": "MS"
            },
            {
                "name": "Missouri",
                "abbreviation": "MO"
            },
            {
                "name": "Montana",
                "abbreviation": "MT"
            },
            {
                "name": "Nebraska",
                "abbreviation": "NE"
            },
            {
                "name": "Nevada",
                "abbreviation": "NV"
            },
            {
                "name": "New Hampshire",
                "abbreviation": "NH"
            },
            {
                "name": "New Jersey",
                "abbreviation": "NJ"
            },
            {
                "name": "New Mexico",
                "abbreviation": "NM"
            },
            {
                "name": "New York",
                "abbreviation": "NY"
            },
            {
                "name": "North Carolina",
                "abbreviation": "NC"
            },
            {
                "name": "North Dakota",
                "abbreviation": "ND"
            },
            {
                "name": "Northern Mariana Islands",
                "abbreviation": "MP"
            },
            {
                "name": "Ohio",
                "abbreviation": "OH"
            },
            {
                "name": "Oklahoma",
                "abbreviation": "OK"
            },
            {
                "name": "Oregon",
                "abbreviation": "OR"
            },
            {
                "name": "Palau",
                "abbreviation": "PW"
            },
            {
                "name": "Pennsylvania",
                "abbreviation": "PA"
            },
            {
                "name": "Puerto Rico",
                "abbreviation": "PR"
            },
            {
                "name": "Rhode Island",
                "abbreviation": "RI"
            },
            {
                "name": "South Carolina",
                "abbreviation": "SC"
            },
            {
                "name": "South Dakota",
                "abbreviation": "SD"
            },
            {
                "name": "Tennessee",
                "abbreviation": "TN"
            },
            {
                "name": "Texas",
                "abbreviation": "TX"
            },
            {
                "name": "Utah",
                "abbreviation": "UT"
            },
            {
                "name": "Vermont",
                "abbreviation": "VT"
            },
            {
                "name": "Virgin Islands",
                "abbreviation": "VI"
            },
            {
                "name": "Virginia",
                "abbreviation": "VA"
            },
            {
                "name": "Washington",
                "abbreviation": "WA"
            },
            {
                "name": "West Virginia",
                "abbreviation": "WV"
            },
            {
                "name": "Wisconsin",
                "abbreviation": "WI"
            },
            {
                "name": "Wyoming",
                "abbreviation": "WY"
            }
        ]
    }


}
